import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import commonEn from '../locales/en/common.json';

export const LANGUAGE_STORAGE_KEY = 'raskh_preferred_language';

// Only the EN locale is statically imported. AR is dynamically loaded by
// LanguageProvider when the user switches to it (or by /ar/* route mount
// via LangBoundary). This keeps ~120 KB of Arabic JSON (~15 KB gzipped)
// out of the landing's main bundle so mobile parse time drops.
const detectInitialLng = () => {
  if (typeof window === 'undefined') return 'en';
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) return stored.toLowerCase().startsWith('ar') ? 'ar' : 'en';
  } catch (err) {
    // localStorage blocked — fall through to htmlTag/navigator
  }
  const htmlLang =
    typeof document !== 'undefined' ? document.documentElement.lang : '';
  if (htmlLang) return htmlLang.toLowerCase().startsWith('ar') ? 'ar' : 'en';
  const nav = window.navigator?.language || '';
  return nav.toLowerCase().startsWith('ar') ? 'ar' : 'en';
};

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: commonEn },
    },
    lng: 'en',
    supportedLngs: ['en', 'ar'],
    fallbackLng: 'en',
    defaultNS: 'common',
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

// Track AR load so we don't fetch twice.
let arLoadPromise = null;

/**
 * Ensures the AR locale bundle is loaded into i18next. Idempotent — the JSON
 * is fetched exactly once. Resolves to the i18n instance for chaining.
 * Called by LanguageProvider before changing language to 'ar'.
 */
export const ensureArResources = async () => {
  if (i18n.hasResourceBundle('ar', 'common')) return i18n;
  if (!arLoadPromise) {
    arLoadPromise = import(/* webpackChunkName: "locale-ar" */ '../locales/ar/common.json')
      .then((mod) => {
        const data = mod && mod.default ? mod.default : mod;
        i18n.addResourceBundle('ar', 'common', data, true, true);
        return i18n;
      })
      .catch((err) => {
        arLoadPromise = null;
        throw err;
      });
  }
  return arLoadPromise;
};

// If the visitor's initial language is AR, kick off the AR load immediately
// (still off the critical path because EN strings paint first).
const initialLng = detectInitialLng();
if (initialLng === 'ar') {
  ensureArResources().then(() => i18n.changeLanguage('ar')).catch(() => {});
}

export default i18n;
